<template>
  <div class="w-full h-full space-y-6 py-6">
    <!-- Заголовок с переключателем активности -->
    <div class="w-full flex items-center justify-between">
      <div class="text-2xl font-medium">Настройки курса</div>

      <div class="flex items-center gap-4">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-gray-600">Статус:</span>
          <span :class="['px-3 py-1 rounded-full text-sm font-medium', course.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800']">
            {{ course.isActive ? 'Активен' : 'Неактивен' }}
          </span>
        </div>
        <label class="inline-flex items-center cursor-pointer">
          <input 
            type="checkbox" 
            :checked="course.isActive" 
            @change="(e) => emit('update:course', { ...props.course, isActive: e.target.checked })" 
            class="sr-only peer"
          >
          <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#574AEA]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#574AEA]"></div>
          <span class="ms-3 text-sm font-medium text-gray-900">Активность курса</span>
        </label>
      </div>
    </div>

    <!-- Информационные карточки -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
      <!-- Дата создания -->
      <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-3">
        <div class="flex items-center gap-3 mb-2">
          <div class="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
          </div>
          <div>
            <div class="text-sm text-blue-600 font-medium">Дата создания</div>
            <div class="text-lg font-semibold text-blue-900">{{ formatDate(course.createdAt) }}</div>
          </div>
        </div>
      </div>

      <!-- Дата обновления -->
      <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-3">
        <div class="flex items-center gap-3 mb-2">
          <div class="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
          </div>
          <div>
            <div class="text-sm text-green-600 font-medium">Последнее обновление</div>
            <div class="text-lg font-semibold text-green-900">{{ formatDate(course.updatedAt) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Основные настройки -->
    <div class="bg-gray-50 rounded-2xl p-4">
      <div class="text-lg font-semibold text-gray-900 mb-4">Основная информация</div>
      
      <div class="space-y-4">
        <div>
          <label for="title" class="block mb-2 text-sm font-medium text-gray-700">Название курса</label>
          <input
            type="text"
            id="title"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#574AEA] focus:border-[#574AEA] transition-colors"
            placeholder="Введите название курса"
            :value="course.title"
            @input="(e) => emit('update:course', { ...props.course, title: e.target.value })"
          />
        </div>

        <div>
          <label for="description" class="block mb-2 text-sm font-medium text-gray-700">Описание курса</label>
          <textarea
            id="description"
            rows="4"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#574AEA] focus:border-[#574AEA] transition-colors resize-none"
            placeholder="Короткое описание курса"
            :value="course.description"
            @input="(e) => emit('update:course', { ...props.course, description: e.target.value })"
          ></textarea>
        </div>
      </div>
    </div>

    <!-- Пользователи -->
    <div class="bg-gray-50 rounded-2xl p-4">
      <div class="flex items-center justify-between mb-4">
        <div class="text-lg font-semibold text-gray-900">Доступ сотрудников</div>
        <div v-if="users.length" class="flex items-center gap-2">
          <label class="inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              :checked="isAllSelected"
              @change="onSelectAll"
              class="w-4 h-4 text-[#574AEA] border-gray-300 rounded focus:ring-[#574AEA]"
            >
            <span class="ml-2 text-sm text-gray-600">Выбрать всех</span>
          </label>
        </div>
      </div>
      <div class="text-sm text-gray-600 mb-4">
        Если сотрудники не указаны, курс не будет доступен никому. Назначьте сотрудников, чтобы открыть доступ.
      </div>

      <div v-if="users.length" class="space-y-3 mb-6">
        <div
          v-for="(user, index) of users"
          :key="index"
          :class="[
            'p-4 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors',
            user.isInactiveUser ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer',
          ]"
          @click="onReassignUser(user)"
        >
          <div class="flex items-center gap-3">
            <input 
              type="checkbox"
              :checked="selectedUserIds.has(String(user.ID))"
              @click.stop="onToggleSelectUser(user.ID)"
              class="w-4 h-4 text-[#574AEA] border-gray-300 rounded focus:ring-[#574AEA] flex-shrink-0"
            >
            <div class="flex-1">
              <UserCard
                :avatarUrl="user.PERSONAL_PHOTO"
                :title="(user.LAST_NAME + ' ' + user.NAME).trim()"
                :subtitle="'Дедлайн: ' + formatDate(latestDueByUser[String(user.ID)]?.dueAt)"
                shape="rounded-full"
              >
                <template #right>
                  <button
                    @click.stop="onDeleteUser(user.ID)"
                    class="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </template>
              </UserCard>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button
          class="w-full h-12 rounded-xl bg-[#574AEA]/10 text-[#574AEA] font-medium hover:bg-[#574AEA]/20 transition-colors flex items-center justify-center gap-2"
          @click="onChoseUser"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
          Добавить сотрудника
        </button>
        <button
          class="w-full h-12 rounded-xl bg-[#574AEA] text-white font-medium hover:bg-[#4b41d3] transition-colors flex items-center justify-center gap-2"
          @click="isRepeatAllOpen = true"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Переназначить всем
        </button>
        <button
          v-if="selectedUserIds.size > 0"
          class="w-full h-12 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
          @click="confirmCancelAssignments"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
          Отменить назначение ({{ selectedUserIds.size }})
        </button>
      </div>
    </div>

    <!-- Опасная зона -->
    <div class="bg-red-50 border border-red-200 rounded-2xl p-4">
      <div class="text-lg font-semibold text-red-900 mb-2">Опасная зона</div>
      <div class="text-sm text-red-700 mb-4">
        При удалении курса будут безвозвратно удалены все уроки, тесты, вопросы и статистика прохождения.
      </div>
      
      <button
        @click="confirmDeleteCourse"
        class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
        </svg>
        Удалить курс
      </button>
    </div>

    <!-- Модалка подтверждения удаления курса -->
    <div v-if="showDeleteCourseConfirm" class="fixed inset-0 bg-white/80 backdrop-blur-sm backdrop-blur-sm z-50 flex items-center justify-center">
      <div class="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl border border-gray-200">
        <div class="text-center">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
          </div>
          <div class="text-lg font-semibold text-gray-900 mb-2">Удалить курс навсегда?</div>
          <p class="text-gray-600 mb-6">
            Вы точно хотите удалить этот курс? При удалении будут безвозвратно удалены:
            <br><br>
            • Все уроки<br>
            • Все тесты и вопросы<br>
            • Вся статистика прохождения<br>
            • Все настройки доступа<br><br>
            Это действие нельзя отменить.
          </p>
          
          <div class="flex gap-3">
            <button
              @click="cancelDeleteCourse"
              class="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              @click="executeDeleteCourse"
              class="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Удалить навсегда
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Модалка подтверждения массовой отмены назначений -->
    <div v-if="showCancelConfirm" class="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
      <div class="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl border border-gray-200">
        <div class="text-center">
          <div class="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
          </div>
          <div class="text-lg font-semibold text-gray-900 mb-2">Отменить назначения?</div>
          <p class="text-gray-600 mb-6">
            Вы точно хотите отменить назначения для {{ selectedUserIds.size }} сотрудников?
            <br><br>
            Это действие отменит все активные назначения для выбранных сотрудников.
          </p>
          
          <div class="flex gap-3">
            <button
              @click="showCancelConfirm = false"
              class="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              @click="executeCancelAssignments"
              class="flex-1 py-2 px-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Отменить назначения
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Модалка назначения сотрудников -->
    <UModal v-model="isAssignOpen">
      <AssignCourseModal :courseId="props.course.id" :preselectedUserIds="props.course.accessibleUsers || []" @close="isAssignOpen = false" @assigned="onAssigned" />
    </UModal>

    <!-- Модалка переназначения для одного пользователя -->
    <UModal v-model="isReassignOpen">
      <AssignCourseModal
        :courseId="props.course.id"
        :defaultSubmitMode="'repeat'"
        :fixedUserIds="reassignUserId ? [reassignUserId] : []"
        @close="isReassignOpen = false"
        @assigned="onReassigned"
      />
    </UModal>

    <!-- Модалка массового переназначения всем -->
    <UModal v-model="isRepeatAllOpen">
      <AssignCourseModal
        :courseId="props.course.id"
        :defaultSubmitMode="'repeat'"
        :preselectedUserIds="props.course.accessibleUsers || []"
        @close="isRepeatAllOpen = false"
        @assigned="onReassigned"
      />
    </UModal>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, onMounted, ref } from 'vue'
import { usePortalStore } from '@/stores/portalStore'
import apiService from '@/services/api'
import { useToast } from "vue-toastification"
import UModal from '@/components/ui/UModal.vue'
import AssignCourseModal from '@/components/course/AssignCourseModal.vue'
import UserCard from '@/components/ui/UserCard.vue'

const props = defineProps({
  course: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:course', 'course-deleted'])

const portalStore = usePortalStore()
const toast = useToast()

// Состояние для модалки удаления курса
const showDeleteCourseConfirm = ref(false)
const isAssignOpen = ref(false)
const isReassignOpen = ref(false)
const isRepeatAllOpen = ref(false)
const reassignUserId = ref(null)
const selectedUserIds = ref(new Set())
const showCancelConfirm = ref(false)

const users = computed(() => {
  const ids = Array.isArray(props.course.accessibleUsers) ? props.course.accessibleUsers : []
  const all = Array.isArray(portalStore.portalData.allUsers) ? portalStore.portalData.allUsers : []
  return ids
    .map((id) => {
      const found = all.find((i) => String(i.ID) === String(id))
      if (found) return found
      const uid = String(id)
      return {
        ID: uid,
        LAST_NAME: '',
        NAME: `Неактивный сотрудник (ID ${uid})`,
        WORK_POSITION: '',
        PERSONAL_PHOTO: '',
        isInactiveUser: true,
      }
    })
})

const assignments = ref([])
const latestDueByUser = computed(() => {
  const map = {}
  for (const a of assignments.value || []) {
    const uid = String(a.userId)
    const prev = map[uid]
    if (!prev || new Date(prev.assignedAt).getTime() < new Date(a.assignedAt).getTime()) {
      map[uid] = a
    }
  }
  return map
})

const formatDate = (dateString) => {
  if (!dateString) return 'Не указано'
  
  const date = new Date(dateString)
  const months = [
    'Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
    'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'
  ]
  
  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  
  return `${day} ${month} ${year}`
}

const onChoseUser = () => {
  isAssignOpen.value = true
}

const onAssigned = async ({ userIds }) => {
  const current = new Set((props.course.accessibleUsers || []).map(String))
  for (const id of (userIds || [])) current.add(String(id))
  emit('update:course', { ...props.course, accessibleUsers: Array.from(current) })
  isAssignOpen.value = false
  await loadAssignments()
}

const isAllSelected = computed(() => {
  return users.value.length > 0 && selectedUserIds.value.size === users.value.length
})

const onToggleSelectUser = (id) => {
  const newSet = new Set(selectedUserIds.value)
  if (newSet.has(String(id))) {
    newSet.delete(String(id))
  } else {
    newSet.add(String(id))
  }
  selectedUserIds.value = newSet
}

const onSelectAll = () => {
  if (isAllSelected.value) {
    selectedUserIds.value = new Set()
  } else {
    selectedUserIds.value = new Set(users.value.map(u => String(u.ID)))
  }
}

const confirmCancelAssignments = () => {
  if (selectedUserIds.value.size === 0) {
    toast.warning('Выберите хотя бы одного сотрудника')
    return
  }
  showCancelConfirm.value = true
}

const executeCancelAssignments = async () => {
  try {
    const userIds = Array.from(selectedUserIds.value)

    // Бэк отменяет ВСЕ активные назначения этих сотрудников (без пагинации)
    // и закрывает связанные задачи Bitrix от постановщика.
    await apiService.cancelAssignments(props.course.id, userIds)

    const current = new Set((props.course.accessibleUsers || []).map(String))
    for (const userId of userIds) {
      current.delete(String(userId))
    }
    emit('update:course', { ...props.course, accessibleUsers: Array.from(current) })

    selectedUserIds.value = new Set()
    showCancelConfirm.value = false
    toast.success(`Назначения отменены для ${userIds.length} сотрудников`)
    await loadAssignments()
  } catch (error) {
    console.error('Ошибка при отмене назначений:', error)
    toast.error('Не удалось отменить назначения')
  }
}

const onDeleteUser = (id) => {
  const users = (props.course.accessibleUsers || []).filter((i) => String(i) !== String(id))
  emit('update:course', { ...props.course, accessibleUsers: users })
}

const loadAssignments = async () => {
  try {
    const res = await apiService.listAssignments(props.course.id)
    assignments.value = Array.isArray(res?.data) ? res.data : []
  } catch { assignments.value = [] }
}

const onReassignUser = (user) => {
  if (user?.isInactiveUser) return
  reassignUserId.value = String(user?.ID)
  isReassignOpen.value = true
}

const onReassigned = async () => {
  isReassignOpen.value = false
  await loadAssignments()
}

onMounted(async () => {
  await loadAssignments()
})

const confirmDeleteCourse = () => {
  showDeleteCourseConfirm.value = true
}

const cancelDeleteCourse = () => {
  showDeleteCourseConfirm.value = false
}

const executeDeleteCourse = async () => {
  try {
    await apiService.deleteCourse(props.course.id)
    emit('course-deleted')
    toast.success('Курс успешно удален')
  } catch (error) {
    console.error('Ошибка при удалении курса:', error)
    toast.error('Не удалось удалить курс')
  } finally {
    showDeleteCourseConfirm.value = false
  }
}
</script>
